import React, { useMemo } from "react";
import { cn, formatAddress, formatDateWithOrdinal } from "@/lib/utils";
import { useGetAgreementQuery } from "@/store/agreements/apis";
import { useParams } from "react-router-dom";
import { MOVE_KINDS_SHORT, SERVICE_TYPES } from "@/constants";
import RateCharges from "./RateCharges";
import { format, parseISO } from "date-fns";
import { Stop } from "@/types";
import { LoadingText } from "@/components/LoadingText";
import Error from "@/components/Error";
import SignatureDisplay from "@/components/SignatureDisplay";

const BOLCell = ({
  label,
  value,
  borderRight,
}: {
  label: string;
  value: string | string[];
  borderRight?: boolean;
}) => {
  return (
    <div
      className={cn(
        "flex flex-col py-1.5 gap-1 min-h-[50px]",
        borderRight && "border-r border-gray-900"
      )}
    >
      <div className="text-xs font-mono uppercase font-semibold">{label}</div>
      <div className="text-base font-medium font-typewriter whitespace-pre-wrap">
        {Array.isArray(value) ? value.join("\r\n") : value}
      </div>
    </div>
  );
};

const Estimate: React.FC = () => {
  const { ref } = useParams();
  const { data, isLoading } = useGetAgreementQuery(ref || "", {
    skip: !ref,
  });

  const agreement = useMemo(() => {
    return data?.agreement;
  }, [data]);

  const move = useMemo(() => {
    return data?.move;
  }, [data]);

  const mover = useMemo(() => {
    return data?.mover;
  }, [data]);

  const customerSignature = agreement?.signatures?.find(
    (s) => s.signed_by === "customer"
  );

  if (isLoading || !agreement)
    return <LoadingText text="Loading agreement details" />;

  if (!mover)
    return (
      <Error error="There was a problem loading this agreement. Please call us and we'll get this sorted out for you." />
    );

  if (!move)
    return (
      <Error error="There was a problem loading this agreement. Please call us and we'll get this sorted out for you." />
    );

  return (
    <div className="bg-amber-50 py-4 px-6 lg:px-4">
      <div className="p-0 lg:p-4 font-paper max-w-[11in] mx-auto lg:border lg:border-gray-900">
        <div className="flex flex-col lg:flex-row lg:justify-between gap-4 lg:gap-0 items-center lg:items-start pb-4 border-b border-gray-900">
          <div>
            <img
              src={`${mover.logo_url}`}
              alt="Logo"
              className="w-24 object-contain"
            />
          </div>
          <div className="text-center lg:text-right">
            <div className="text-base font-semibold mb-1">{mover.name}</div>
            <div className="text-sm leading-snug font-mono">
              <div>
                {mover.street}, {mover.city} {mover.state} {mover.zip}
              </div>
              <div>{mover.email}</div>
              <div>{mover.business_phone}</div>
            </div>
          </div>
        </div>

        <div className="py-4 text-center border-b border-gray-900 space-y-2">
          <h1 className="font-paper text-2xl">Moving Estimate</h1>
          <p className="text-sm font-mono">
            Generated on {format(parseISO(agreement?.created_at), "MM/dd/yyyy")}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6 text-sm leading-relaxed border-b border-gray-900">
          <BOLCell label="Shipper" value={move.customer_name} borderRight />
          <BOLCell label="Job Number" value={move.number} borderRight />
          <BOLCell
            label="Estimate Type"
            value={
              move.billing_type === "hourly"
                ? "Hourly (Non Binding)"
                : "Flat Rate (Binding)"
            }
          />
        </div>

        <div className="grid grid-cols-3 gap-6 text-sm leading-relaxed border-b border-gray-900">
          <BOLCell
            label="Service"
            value={
              SERVICE_TYPES[move.service as keyof typeof SERVICE_TYPES] ||
              move.service ||
              "N/A"
            }
            borderRight
          />
          <BOLCell
            label="Service Date(s)"
            value={formatDateWithOrdinal(parseISO(move.service_date))}
            borderRight
          />
          <BOLCell
            label="Details"
            value={MOVE_KINDS_SHORT[move.kind as keyof typeof MOVE_KINDS_SHORT]}
          />
        </div>
        <div className="grid grid-cols-3 gap-6 text-sm leading-relaxed border-b border-gray-900">
          <BOLCell
            label="Origin"
            value={formatAddress(move.origin)}
            borderRight
          />
          <BOLCell
            label="Intermediate Stops"
            value={
              move.stops?.length
                ? move.stops?.map((stop: Stop) => formatAddress(stop))
                : "N/A"
            }
            borderRight
          />
          <BOLCell
            label="Destination"
            value={formatAddress(move.destination)}
          />
        </div>

        <div className="grid grid-cols-2 gap-6 text-sm leading-relaxed border-b border-gray-900">
          <BOLCell label="Notes" value={move.notes_customer} />
        </div>

        <div className="my-4 grid grid-cols-1 gap-4">
          <div className="col-span-1">
            <div>
              <h4 className="font-paper text-lg font-medium">
                Estimated Rate and Charges
              </h4>
              <RateCharges move={move} />
            </div>
          </div>

          <div className="col-span-1 space-y-4">
            <div>
              <h4 className="font-paper text-lg font-medium">
                Carrier&apos;s Liability
              </h4>
              <p className="font-paper text-sm prose max-w-prose">
                By default, Basic Value Protection is included in your move, and
                covers your items at $0.60 per pound per article, at no cost to
                you. This means that you'll be paid 60 cents per pound for the
                estimated weight of a lost or damaged item, regardless of the
                actual value of the item.
                <br />
                Note: Valuation is not insurance coverage. We suggest you
                purchase third-party insurance to protect your property.
              </p>
            </div>
            <div>
              <h4 className="font-paper text-base font-medium">
                Payment Terms
              </h4>
              <p className="font-paper text-sm">
                Payment and any balances for the move are due in full upon
                completion of the job while crew is on site.
              </p>
            </div>
            {mover?.mover_configuration?.ca_ddt_enabled && (
              <div>
                <h4 className="font-paper text-base font-medium">
                  Double Drive Time (California Specific)
                </h4>
                <p className="font-paper text-sm">
                  As per CA state regulations, carriers are required to charge
                  Double Drive Time for hourly moves. This is calculated as
                  twice the drive time it takes to travel from the point of
                  origin (pickup) of the move to the final destination charged
                  at the hourly labor rate of the move.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="py-4 border-t border-gray-900">
          <p className="font-paper">
            I understand that this estimate has been generated based on the
            information provided by me. Any changes in the scope may result in
            additional charges.{" "}
            {move.billing_type == "flat_rate"
              ? "I accept the rates and charges set forth in this estimate."
              : "I agree to the rates and charges set forth in this estimate and understand that the final amount may differ."}
          </p>
          {mover.mover_configuration.signed_estimates && !customerSignature && (
            <div>
              <p className="font-paper">
                [Customer Signature] (customer has not signed the estimate yet)
              </p>
            </div>
          )}
          <div className="mt-4">
            {customerSignature && (
              <SignatureDisplay
                signature={customerSignature}
                label="Shipper Signature"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Estimate;
